import React, { useMemo } from "react";
import { Card, ListGroup } from "react-bootstrap";
import { Link } from "react-router";

import { useAppCollection } from "../../api/firestoreWrappers.generated.jsx";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import { useUser } from "../../contexts/user_provider";
import { ResultListGroupItems } from "../ResultListGroupItems/index.jsx";
import { isFilePublished } from "../ResultsList/index.jsx";

export default function ResultCardList({
    results = [],
    title = "Results",
    url,
    className,
    showTimestamp = true,
    showGoToMission,
    extraDropdownItems,
}) {
    const { getUserIsAdmin } = useUser();
    const isAdmin = useMemo(() => getUserIsAdmin(), [getUserIsAdmin]);
    const [allApps] = useAppCollection([], { type: "map" });

    const filteredAndSortedResults = React.useMemo(() => {
        if (!results || !allApps) return [];

        return results
            .map((result) => {
                if (!Array.isArray(result.files)) return null;

                const publishedFiles = result.files.filter((file) =>
                    isFilePublished(file, isAdmin),
                );

                if (publishedFiles.length === 0) return null;

                return {
                    ...result,
                    files: publishedFiles,
                };
            })
            .filter(Boolean)
            .sort((a, b) => {
                const nameA = allApps[a?.app?.id]?.name ?? "";
                const nameB = allApps[b?.app?.id]?.name ?? "";

                return nameA.localeCompare(nameB, undefined, {
                    sensitivity: "base",
                    numeric: true,
                });
            });
    }, [results, isAdmin, allApps]);

    const totalFiles = React.useMemo(() => {
        return filteredAndSortedResults.reduce(
            (sum, item) => sum + item.files.length,
            0,
        );
    }, [filteredAndSortedResults]);

    if (filteredAndSortedResults.length === 0 || totalFiles === 0) {
        return (
            <Card className={className}>
                <Card.Body>
                    <div className="text-muted text-center">No Results</div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className={className}>
            <Card.Header className="d-flex justify-content-between">
                <div style={{ fontWeight: "bold" }}>{title}</div>
                {url && (
                    <div>
                        <Link to={url}>View all</Link>
                    </div>
                )}
            </Card.Header>

            <ListGroup variant="flush">
                {filteredAndSortedResults.map((result, resultIndex) =>
                    result?.files?.length > 0 ? (
                        <ListGroup.Item
                            key={`result-${result.id || resultIndex}`}
                            className="px-3 py-2"
                        >
                            {/* App Name */}
                            <div className="fw-bold">
                                {allApps[result?.app?.id]?.name ||
                                    "Unnamed App"}
                            </div>

                            {/* Date */}
                            {showTimestamp && result.timestamp && (
                                <div className="text-muted small mb-1">
                                    <TimeDisplay
                                        timestamp={result.timestamp.seconds}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        hideDateIfToday={false}
                                        organizationId={result.organizationId}
                                        locationId={result.locationId}
                                    />
                                </div>
                            )}

                            <ListGroup variant="flush">
                                <ResultListGroupItems
                                    className={"py-1 px-0"}
                                    results={[result]}
                                    isAdmin={isAdmin}
                                    showTimestamp={false}
                                    showGoToMission={showGoToMission}
                                    extraDropdownItems={extraDropdownItems}
                                />
                            </ListGroup>
                        </ListGroup.Item>
                    ) : null,
                )}
            </ListGroup>
        </Card>
    );
}
